// Option templates (optionTemplates.*): saved good / better / best option sets, kept as price-book
// items and quantities only, and priced from the price book when they're used. The builder's form in
// plain text, turned into the action's input or the one thing to fix; and a template turned into an
// estimate draft's options (EstimateEditor's "Start from a template").

import { dollars, type DraftOption } from '../job/job-model.ts';
import { newId } from '../shared/ids.ts';
import { lineTotal, MAX_ESTIMATE_OPTIONS } from '@flowology/domain';

export type Tier = 'good' | 'better' | 'best';
export const TIERS: readonly (readonly [Tier | '', string])[] = [
  ['', 'No tier'],
  ['good', 'Good'],
  ['better', 'Better'],
  ['best', 'Best'],
];
/** As many as an estimate offers (packages/domain), so a template can always become one. */
export const MAX_OPTIONS = MAX_ESTIMATE_OPTIONS;
export const MAX_LINES = 30;
const QUANTITY = /^\d{1,9}(\.\d{1,3})?$/;

export interface TemplateLine {
  pricebookItemId: string;
  quantity: string;
  code: string | null;
  name: string | null;
  /** Today's price; null when the price book no longer offers the item. */
  unitPriceCents: number | null;
  active: boolean;
}
export interface OptionTemplate {
  id: string;
  name: string;
  businessUnitId: string | null;
  jobTypeId: string | null;
  active: boolean;
  rowVersion: number;
  options: {
    title: string;
    tier: Tier | null;
    summary: string | null;
    lines: TemplateLine[];
    totalCents: number;
  }[];
}

export interface TplOptionForm {
  title: string;
  tier: Tier | '';
  summary: string;
  lines: TemplateLine[];
}
export interface TplForm {
  name: string;
  businessUnitId: string;
  jobTypeId: string;
  options: TplOptionForm[];
}

const TIER_ORDER: Tier[] = ['good', 'better', 'best'];
export const blankTplOption = (i: number): TplOptionForm => {
  const tier = TIER_ORDER[i] ?? '';
  return {
    title: tier ? tier[0]!.toUpperCase() + tier.slice(1) : `Option ${i + 1}`,
    tier,
    summary: '',
    lines: [],
  };
};
export const emptyTemplate = (): TplForm => ({
  name: '',
  businessUnitId: '',
  jobTypeId: '',
  options: [blankTplOption(0)],
});
export const templateForm = (t: OptionTemplate): TplForm => ({
  name: t.name,
  businessUnitId: t.businessUnitId ?? '',
  jobTypeId: t.jobTypeId ?? '',
  options: t.options.map((o) => ({
    title: o.title,
    tier: o.tier ?? '',
    summary: o.summary ?? '',
    lines: o.lines.map((l) => ({ ...l })),
  })),
});

/** A price-book item picked into an option: today's price, quantity 1. */
export const tplLine = (item: {
  id: string;
  code: string;
  name: string;
  priceCents: number;
}): TemplateLine => ({
  pricebookItemId: item.id,
  quantity: '1',
  code: item.code,
  name: item.name,
  unitPriceCents: item.priceCents,
  active: true,
});

/** What an option comes to at today's prices, its offered lines only (cents), or null with a bad quantity. */
export function tplOptionCents(o: Pick<TplOptionForm, 'lines'>): number | null {
  let t = 0;
  for (const l of o.lines) {
    if (!QUANTITY.test(l.quantity.trim())) return null;
    // the estimate's own exact math (BigInt thousandths, half up): 1.005 × $1.00 is $1.01, as on the estimate
    if (l.unitPriceCents !== null) t += lineTotal(l.quantity.trim(), l.unitPriceCents);
  }
  return t;
}

type Built = { input: Record<string, unknown> } | { error: string };

interface OptionInput {
  title: string;
  tier: Tier | null;
  summary: string | null;
  lines: { pricebookItemId: string; quantity: string }[];
}
const optionsInput = (f: TplForm): { error: string } | { options: OptionInput[] } => {
  if (!f.options.length) return { error: 'Add at least one option.' };
  if (f.options.length > MAX_OPTIONS) return { error: `A template has at most ${MAX_OPTIONS} options.` };
  const out: OptionInput[] = [];
  for (const [i, o] of f.options.entries()) {
    const n = `Option ${i + 1}`;
    if (!o.title.trim()) return { error: `${n} needs a title.` };
    if (!o.lines.length) return { error: `${n} needs at least one line from the price book.` };
    if (o.lines.length > MAX_LINES) return { error: `${n} has more than ${MAX_LINES} lines.` };
    const gone = o.lines.find((l) => !l.active);
    if (gone)
      return {
        error: `${n}: the price book no longer offers ${gone.name ?? 'one of its items'}. Remove it.`,
      };
    for (const [j, l] of o.lines.entries())
      if (!QUANTITY.test(l.quantity.trim()))
        return { error: `${n}, line ${j + 1}: a quantity like 1 or 1.5.` };
    out.push({
      title: o.title.trim(),
      tier: o.tier || null,
      summary: o.summary.trim() || null,
      lines: o.lines.map((l) => ({ pricebookItemId: l.pricebookItemId, quantity: l.quantity.trim() })),
    });
  }
  return { options: out };
};

/** optionTemplates.create for a new template; .update with only what changed (and its rowVersion). */
export function templateInput(was: OptionTemplate | null, f: TplForm): Built {
  const name = f.name.trim();
  if (!name) return { error: 'Give the template a name.' };
  const o = optionsInput(f);
  if ('error' in o) return { error: o.error };
  const where = { businessUnitId: f.businessUnitId || null, jobTypeId: f.jobTypeId || null };
  if (!was) return { input: { name, ...where, options: o.options } };
  const before = optionsInput(templateForm(was));
  const changed: Record<string, unknown> = {};
  if (name !== was.name) changed.name = name;
  if (where.businessUnitId !== was.businessUnitId) changed.businessUnitId = where.businessUnitId;
  if (where.jobTypeId !== was.jobTypeId) changed.jobTypeId = where.jobTypeId;
  if (!('options' in before) || JSON.stringify(o.options) !== JSON.stringify(before.options))
    changed.options = o.options;
  if (!Object.keys(changed).length) return { error: 'Nothing has changed yet.' };
  return { input: { id: was.id, rowVersion: was.rowVersion, ...changed } };
}

/** Where a template is offered, in words: "Every job", "Service", "Water heater", or both with a dot. */
export function templateScope(
  t: Pick<OptionTemplate, 'businessUnitId' | 'jobTypeId'>,
  units: readonly { id: string; name: string }[],
  types: readonly { id: string; name: string }[],
): string {
  const u = t.businessUnitId ? (units.find((x) => x.id === t.businessUnitId)?.name ?? 'A department') : null;
  const j = t.jobTypeId ? (types.find((x) => x.id === t.jobTypeId)?.name ?? 'A job type') : null;
  return [u, j].filter(Boolean).join(' · ') || 'Every job';
}

/**
 * A template as an estimate draft's options (EstimateEditor): each line at today's book price, new
 * ids, so saving creates them. Items the price book stopped offering are left out and counted.
 */
export function draftFromTemplate(t: OptionTemplate): { options: DraftOption[]; skipped: number } {
  let skipped = 0;
  const options = t.options.map((o) => ({
    optionId: newId(),
    title: o.title,
    tier: (o.tier ?? '') as DraftOption['tier'],
    summary: o.summary ?? '',
    lines: o.lines.flatMap((l) => {
      if (!l.active || l.unitPriceCents === null) {
        skipped++;
        return [];
      }
      return [
        {
          lineId: newId(),
          pricebookItemId: l.pricebookItemId,
          description: l.name ?? '',
          quantity: l.quantity,
          price: dollars(l.unitPriceCents),
          bookPriceCents: l.unitPriceCents,
          cost: '',
          reason: '',
        },
      ];
    }),
  }));
  return { options: options.filter((o) => o.lines.length), skipped };
}
